import React, { useState, useEffect } from 'react';
import { Card, Grid, Icon, Typography, Select, MenuItem, Box, IconButton, Badge } from '@mui/material';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import EscortTable from './EscortTable';
import MonitoringModal from './MonitoringModal';
import apiClient, { SOCKET_URL } from 'Utils/apiClient';
import { io } from 'socket.io-client';

const statesList = [
  'All States', 'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue', 'Borno',
  'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu', 'FCT - Abuja', 'Gombe', 'Imo', 'Jigawa',
  'Kaduna', 'Kano', 'Katsina', 'Kebbi', 'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo',
  'Osun', 'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara'
];

function VirtualEscortDashboard() {
  const [selectedState, setSelectedState] = useState('All States');
  const [escorts, setEscorts] = useState([]);
  const [isVoiceMuted, setIsVoiceMuted] = useState(false);
  const [criticalCount, setCriticalCount] = useState(0);
  const [attentionCount, setAttentionCount] = useState(0);
  const [selectedEscort, setSelectedEscort] = useState(null);
  const [isMonitoringModalOpen, setIsMonitoringModalOpen] = useState(false);

  // Stats
  const trackedNowCount = escorts.length;

  useEffect(() => {
    fetchActiveEscorts();
    setupSocket();
  }, [selectedState]);

  const fetchActiveEscorts = async () => {
    try {
      const response = await apiClient.get('/virtual-escort/admin/active', {
        params: { state: selectedState }
      });
      if (response && response.success) {
        const escortData = response.data || [];
        setEscorts(escortData);
        updateCounts(escortData);
      }
    } catch (error) {
      console.error('Failed to fetch active escorts', error);
    }
  };

  const updateCounts = (data) => {
    const crit = data.filter(e => e.priority === 'Critical').length;
    const attn = data.filter(e => e.priority === 'Needs Attention').length;
    setCriticalCount(crit);
    setAttentionCount(attn);
  };

  const setupSocket = () => {
    const socket = io(SOCKET_URL);
    // User role needs to be determined
    socket.emit('joinRoom', 'superadmin');
    
    socket.on('virtualescort_update', (data) => {
      setEscorts((prev) => {
        const updated = prev.map(e => e.escortId === data.escortId ? { ...e, ...data } : e);
        updateCounts(updated);
        return [...updated].sort((a, b) => {
          if (a.priority === 'Critical') return -1;
          if (b.priority === 'Critical') return 1;
          if (a.priority === 'Needs Attention') return -1;
          if (b.priority === 'Needs Attention') return 1;
          return 0;
        });
      });
      
      if (selectedEscort && selectedEscort.escortId === data.escortId) {
        setSelectedEscort(prev => ({...prev, ...data}));
      }
    });

    socket.on('virtualescort_started', (data) => {
       fetchActiveEscorts(); // Re-fetch to get full details
    });

    socket.on('virtualescort_resolved', (data) => {
      setEscorts((prev) => prev.filter(e => e.escortId !== data.escortId));
      if (selectedEscort && selectedEscort.escortId === data.escortId) {
        setIsMonitoringModalOpen(false);
      }
    });

    socket.on('virtualescort_voice_alert', (data) => {
      if (!isVoiceMuted) {
        const msg = new SpeechSynthesisUtterance(data.message);
        window.speechSynthesis.speak(msg);
      }
    });

    return () => socket.disconnect();
  };

  const handleOpenMonitoring = (escort) => {
    setSelectedEscort(escort);
    setIsMonitoringModalOpen(true);
  };

  return (
    <DashboardLayout>
      {/* Top Bar matching requirements */}
      <Box sx={{ backgroundColor: '#1A73E8', color: '#fff', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2, mb: 3 }}>
        <Select
          value={selectedState}
          onChange={(e) => setSelectedState(e.target.value)}
          sx={{ backgroundColor: '#fff', minWidth: 200, height: 40 }}
        >
          {statesList.map(state => (
            <MenuItem key={state} value={state}>{state}</MenuItem>
          ))}
        </Select>

        <MDTypography variant="h5" color="white" fontWeight="bold">
          ALL-STATE ESCORT MANAGEMENT DASHBOARD
          <MDTypography variant="button" color="white" display="block" opacity={0.8}>
            Real-time monitoring of all user, driver and cargo escorts across Nigeria
          </MDTypography>
        </MDTypography>

        <Box display="flex" alignItems="center">
          <IconButton onClick={() => setIsVoiceMuted(!isVoiceMuted)} color="inherit">
            <Icon>{isVoiceMuted ? 'volume_off' : 'volume_up'}</Icon>
          </IconButton>
          <IconButton color="inherit">
            <Badge badgeContent={criticalCount + attentionCount} color="error">
              <Icon>notifications</Icon>
            </Badge>
          </IconButton>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, backgroundColor: '#E3F2FD', cursor: 'pointer' }}>
            <MDTypography variant="h6">Tracked Now</MDTypography>
            <MDTypography variant="h3">{trackedNowCount}</MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, backgroundColor: '#FFEBEE', cursor: 'pointer' }}>
            <MDTypography variant="h6" color="error">Critical</MDTypography>
            <MDTypography variant="h3" color="error">{criticalCount}</MDTypography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, backgroundColor: '#FFF3E0', cursor: 'pointer' }}>
            <MDTypography variant="h6" color="warning">Needs Attention</MDTypography>
            <MDTypography variant="h3" color="warning">{attentionCount}</MDTypography>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Typography variant="button" sx={{ mr: 3, cursor: 'pointer', color: 'blue' }}>Live Escorts</Typography>
        <Typography variant="button" sx={{ mr: 3, cursor: 'pointer' }}>Resolved History</Typography>
        <Typography variant="button" sx={{ mr: 3, cursor: 'pointer' }}>Audit Log</Typography>
        <Typography variant="button" sx={{ cursor: 'pointer' }}>Settings</Typography>
      </Box>

      {/* Table */}
      <Card>
        <MDBox p={3}>
          <EscortTable escorts={escorts} onRowClick={handleOpenMonitoring} />
        </MDBox>
      </Card>

      {isMonitoringModalOpen && selectedEscort && (
        <MonitoringModal 
          open={isMonitoringModalOpen} 
          onClose={() => setIsMonitoringModalOpen(false)} 
          escort={selectedEscort}
        />
      )}
    </DashboardLayout>
  );
}

export default VirtualEscortDashboard;
